/**
 * Loot: crates, coins and health packs. See `@chaos/protocol`'s `loot.ts` for
 * what each one is FOR; this file is how the sim decides them.
 *
 * FOUR RULES, each one a money decision:
 *
 * 1. ITS OWN RNG STREAM. Loot draws from `loot.rngState`, seeded from the match
 *    seed, never from `world.rngState`. The seed commitment still covers every
 *    crate and every health pack (same seed, same draws), and adding loot moved
 *    nothing that was already seeded: spawn pads, the zone centre and every
 *    barrage shell land exactly where they did before this file existed.
 *
 * 2. NOTHING HERE PAYS THE TIEBREAK. Breaking a crate is not `damageDealt` and
 *    not ult charge, exactly like popping a decoy. `damageDealt` decides
 *    simultaneous deaths and clock expiries — a pot — and a lockbox is not an
 *    opponent.
 *
 * 3. HEALTH ONLY IN THE CENTRE. Health packs are declared inside a small disc
 *    about the zone centre and nowhere else (GAME-DESIGN §2). The zone centre,
 *    not the arena centre, because that is where the fight converges.
 *
 * 4. NO FRESH ROLL IN THE ENDGAME. Once a match that started with three or
 *    more players is down to two, no new health pack is declared (a 1v1 and a
 *    practice room have no endgame) — the director's Rule 3 for the same reason: an
 *    endgame resolves on what is already on the floor, never on a heal that
 *    happened to land beside one of the last two.
 */
import { HEALTH_PACK_HEAL, COIN_CAP, CRATE_HEALTH, coinDamageMultiplier, } from "@chaos/protocol";
import { bodyHalfHeight, bodyRadius, findPlayer, maxHealth } from "./body.js";
import { isInvisible } from "./effects.js";
import { onArenaFloor, platformTop } from "./map.js";
import { clamp, msToTicks } from "./math.js";
import { hashSeed, nextPointInDisc } from "./rng.js";
import { dist2XZ } from "./vec.js";
// ---------------------------------------------------------------------------
// Tuning. Sim-only: nothing outside the sim quotes these.
// ---------------------------------------------------------------------------
/** A crate's footprint and height. A bit wider and shorter than a body. */
export const CRATE_RADIUS = 0.8;
export const CRATE_HALF_HEIGHT = 0.7;
/** Crates per match: two more than the lobby, within these bounds. */
const CRATE_MIN = 4;
const CRATE_MAX = 10;
/** Crates are placed inside this fraction of the zone's opening radius. */
const CRATE_SPREAD = 0.8;
/** No two crates closer than this, and none on top of a spawn pad. */
const CRATE_MIN_GAP = 7;
const CRATE_SPAWN_CLEARANCE = 5;
/**
 * How far above the lowest spawn floor a crate may sit. A lockbox on top of a
 * pillar nobody can climb is a lockbox nobody can open.
 */
const CRATE_MAX_ELEVATION = 4;
/** Practice rooms only: a broken crate re-stocks after this long. */
const CRATE_RESTOCK_MS = 25_000;
/** How close a body must come to take a pickup, beyond its own radius. */
const PICKUP_RADIUS = 0.9;
/** Feet within this of the pickup's floor. A ledge above it does not count. */
const PICKUP_VERTICAL_REACH = 1.6;
/**
 * A dropped holding cannot be taken for this long. Long enough to SEE it land
 * — a stack that vanished into the killer on the tick it appeared would read
 * as the coins simply being transferred, which is a different game.
 */
const ESTATE_READY_MS = 300;
/** How far above a victim's feet a surface may be and still count as under them. */
const FEET_TOLERANCE = 0.5;
/** First health pack this long after the drop, then one per interval. */
const HEALTH_PACK_FIRST_MS = 12_000;
const HEALTH_PACK_INTERVAL_MS = 18_000;
/** Most health packs on the floor at once. */
const HEALTH_PACK_MAX_ACTIVE = 2;
/** Declared within this fraction of the arena radius of the zone centre… */
const HEALTH_PACK_RADIUS_FRACTION = 0.18;
/** …clamped to these bounds, and never wider than half the live zone. */
const HEALTH_PACK_RADIUS_MIN = 3;
const HEALTH_PACK_RADIUS_MAX = 12;
/** Placement attempts before a declaration gives up until the next second. */
const PLACE_TRIES = 16;
/**
 * Seeds the loot for a fresh world and places every crate.
 *
 * Called from `createWorld` after the zone exists, so crates are placed inside
 * the zone's opening circle.
 */
export function createLoot(world) {
    const loot = {
        // Salted so the stream is independent of the match RNG but still a pure
        // function of the committed seed.
        rngState: hashSeed(`${world.seed}|loot`),
        nextId: 0,
        slots: [],
        crates: [],
        pickups: [],
        nextHealthPackTick: world.dropEndTick + msToTicks(HEALTH_PACK_FIRST_MS),
        restocks: [],
    };
    const count = clamp(world.players.length + 2, CRATE_MIN, CRATE_MAX);
    const baseline = spawnBaseline(world);
    const spread = world.zone.startRadius * CRATE_SPREAD;
    for (let n = 0; n < count; n++) {
        for (let attempt = 0; attempt < PLACE_TRIES * 2; attempt++) {
            const o = nextPointInDisc(loot, spread);
            const x = world.zone.centerX + o.dx;
            const z = world.zone.centerZ + o.dz;
            const ground = standableGround(world, x, z);
            if (ground === null)
                continue;
            if (baseline !== null && ground > baseline + CRATE_MAX_ELEVATION)
                continue;
            if (world.map.spawns.some((s) => dist2XZ(s.x, s.z, x, z) < CRATE_SPAWN_CLEARANCE ** 2))
                continue;
            if (loot.slots.some((s) => dist2XZ(s.x, s.z, x, z) < CRATE_MIN_GAP ** 2))
                continue;
            loot.slots.push({ x, y: ground, z });
            loot.crates.push(makeCrate(loot, loot.slots.length - 1));
            break;
        }
    }
    return loot;
}
function makeCrate(loot, slot) {
    const s = loot.slots[slot];
    loot.nextId += 1;
    return { id: `crate#${loot.nextId}`, slot, x: s.x, y: s.y + CRATE_HALF_HEIGHT, z: s.z, health: CRATE_HEALTH };
}
// ---------------------------------------------------------------------------
// Ground
// ---------------------------------------------------------------------------
const scratch = [];
/**
 * The top of whatever a thing dropped from the sky at (x, z) would land on, or
 * null over the void. Highest solid surface wins — the same answer gravity
 * gives — so nothing is ever placed inside a box or under a ledge.
 */
export function groundAt(world, x, z, maxY = Infinity) {
    let bestY = -Infinity;
    let kind = "normal";
    for (const index of world.hash.queryInto(x, z, scratch)) {
        const p = world.platforms[index];
        if (!p || !p.solid)
            continue;
        if (Math.abs(x - p.x) > p.hx || Math.abs(z - p.z) > p.hz)
            continue;
        const top = platformTop(p);
        if (top > bestY && top <= maxY) {
            bestY = top;
            kind = p.surface.kind;
        }
    }
    const floor = world.map.floor;
    if (floor.kind === "solid" && onArenaFloor(world.map, x, z) && floor.y > bestY && floor.y <= maxY) {
        bestY = floor.y;
        kind = "normal";
    }
    if (bestY === -Infinity || bestY <= world.map.killY)
        return null;
    return { y: bestY, kind };
}
/** Ground a crate or a health pack may be PLACED on: not a hazard, not a launcher. */
function standableGround(world, x, z) {
    const g = groundAt(world, x, z);
    if (g === null || g.kind === "hazard" || g.kind === "bounce")
        return null;
    return g.y;
}
/** The lowest floor under any spawn pad: the arena's walking level. */
function spawnBaseline(world) {
    let low = null;
    for (const s of world.map.spawns) {
        const g = groundAt(world, s.x, s.z);
        if (g !== null && (low === null || g.y < low))
            low = g.y;
    }
    return low;
}
// ---------------------------------------------------------------------------
// Crate damage — called from combat.ts by the shapes that deal DAMAGE to an
// area or a line: melee cones, plain spheres, dash lines, bullets, piercing
// orbs, shell bursts and barrage shells. Chain, pull and swap target bodies
// by definition, and traps and auras trigger on bodies, so none of those
// touch a crate.
// ---------------------------------------------------------------------------
/**
 * Deals `raw` to a crate, and breaks it at zero.
 *
 * The attacker's coins apply, so a stake makes you faster at farming more of
 * them — the Brawl Stars snowball, bounded by COIN_CAP. Nothing is credited:
 * see rule 2 at the top of this file.
 *
 * Returns true when the crate broke and left `loot.crates`, so a caller walking
 * that list knows not to step past the element that slid into its place.
 */
export function damageCrate(world, crate, raw, attackerId, abilityId, events) {
    const loot = world.loot;
    if (!loot || crate.health <= 0 || raw <= 0)
        return false;
    // Same fly-in rule as bodies: nobody lands already farming.
    if (world.tick <= world.dropEndTick)
        return false;
    const attacker = findPlayer(world, attackerId);
    // See `isGhost`.
    if (attacker !== null && attacker.phase === "alive" && isInvisible(attacker))
        return false;
    const dmg = raw * coinDamageMultiplier(attacker?.coins ?? 0);
    crate.health -= dmg;
    events.push({
        t: "hit",
        tick: world.tick,
        attackerId,
        targetId: crate.id,
        abilityId,
        damage: dmg,
        x: crate.x,
        y: crate.y,
        z: crate.z,
    });
    if (crate.health > 0)
        return false;
    const i = loot.crates.indexOf(crate);
    if (i >= 0)
        loot.crates.splice(i, 1);
    const floorY = crate.y - CRATE_HALF_HEIGHT;
    events.push({ t: "crate_broken", tick: world.tick, crateId: crate.id, attackerId, x: crate.x, y: floorY, z: crate.z });
    dropPickup(world, loot, "coin", 1, crate.x, floorY, crate.z, world.tick, events);
    if (world.mode === "casual") {
        loot.restocks.push({ slot: crate.slot, tick: world.tick + msToTicks(CRATE_RESTOCK_MS) });
    }
    return true;
}
/**
 * Every crate a hit shape's predicate accepts, in list order. A crate that
 * breaks is spliced out, and the next one slides into its index — so the index
 * only advances past a crate that is still there. No copy of the list.
 */
export function damageCratesWhere(world, accept, raw, attackerId, abilityId, events) {
    const crates = world.loot?.crates;
    if (!crates)
        return;
    for (let i = 0; i < crates.length;) {
        const v = crates[i];
        if (accept(v) && damageCrate(world, v, raw, attackerId, abilityId, events))
            continue;
        i++;
    }
}
// ---------------------------------------------------------------------------
// Drops
// ---------------------------------------------------------------------------
function dropPickup(world, loot, kind, amount, x, y, z, readyTick, events) {
    loot.nextId += 1;
    const pickup = { id: `loot#${loot.nextId}`, kind, x, y, z, amount, readyTick };
    loot.pickups.push(pickup);
    events.push({ t: "loot_spawn", tick: world.tick, pickupId: pickup.id, kind, amount, x, y, z });
}
/**
 * An eliminated player's holding hits the floor where they fell — never fewer
 * than one coin, so every elimination is worth walking to.
 *
 * A player who fell into the void has no floor under them; the holding lands
 * at the zone centre instead, where the fight is. If even that is over the
 * void the coins are gone, which only an arena with no floor at its centre
 * can do.
 */
export function dropEstate(world, victim, events) {
    const loot = world.loot;
    const amount = Math.max(1, victim.coins);
    victim.coins = 0;
    if (!loot)
        return;
    let x = victim.x;
    let z = victim.z;
    // The floor UNDER them, not the top of whatever is above: a player killed
    // beneath a ledge drops their holding where they stood, not on the ledge.
    let ground = groundAt(world, x, z, victim.y - bodyHalfHeight(world) + FEET_TOLERANCE);
    if (ground === null) {
        x = world.zone.centerX;
        z = world.zone.centerZ;
        ground = groundAt(world, x, z);
    }
    if (ground === null)
        return;
    dropPickup(world, loot, "coin", amount, x, ground.y, z, world.tick + msToTicks(ESTATE_READY_MS), events);
}
// ---------------------------------------------------------------------------
// The per-tick step
// ---------------------------------------------------------------------------
/**
 * Declares health packs, re-stocks practice crates, and hands out pickups.
 *
 * Runs AFTER eliminations: a player the tick's damage already killed does not
 * get saved by a heal they happened to be standing on.
 */
export function stepLoot(world, events) {
    const loot = world.loot;
    if (!loot)
        return;
    if (loot.restocks.length > 0)
        restock(world, loot);
    if (world.tick >= loot.nextHealthPackTick)
        declareHealthPack(world, loot, events);
    if (loot.pickups.length > 0)
        collect(world, loot, events);
}
function restock(world, loot) {
    if (!loot.restocks.some((r) => world.tick >= r.tick))
        return;
    const due = loot.restocks.filter((r) => world.tick >= r.tick);
    loot.restocks = loot.restocks.filter((r) => world.tick < r.tick);
    for (const r of due)
        loot.crates.push(makeCrate(loot, r.slot));
}
function declareHealthPack(world, loot, events) {
    let alive = 0;
    for (const p of world.players)
        if (p.phase === "alive")
            alive++;
    // The final two of a match that STARTED bigger. `alive <= 2` alone was true
    // from the first tick of any two-player world — a 1v1, the gym with one
    // dummy — so those never saw a single health pack (found by playing it).
    // Practice rooms respawn everyone, so they have no endgame at all.
    const endgame = world.mode !== "casual" && world.players.length >= 3 && alive <= 2;
    let active = 0;
    for (const p of loot.pickups)
        if (p.kind === "health_pack")
            active++;
    if (endgame || active >= HEALTH_PACK_MAX_ACTIVE) {
        loot.nextHealthPackTick = world.tick + msToTicks(HEALTH_PACK_INTERVAL_MS);
        return;
    }
    const zone = world.zone;
    const radius = healthPackRadius(world);
    for (let attempt = 0; attempt < PLACE_TRIES; attempt++) {
        const o = nextPointInDisc(loot, radius);
        const x = zone.centerX + o.dx;
        const z = zone.centerZ + o.dz;
        const ground = standableGround(world, x, z);
        if (ground === null)
            continue;
        dropPickup(world, loot, "health_pack", 1, x, ground, z, world.tick, events);
        loot.nextHealthPackTick = world.tick + msToTicks(HEALTH_PACK_INTERVAL_MS);
        return;
    }
    // Nowhere to put it this time — an arena whose centre is mostly void. Try
    // again in a second rather than skipping a whole interval.
    loot.nextHealthPackTick = world.tick + msToTicks(1_000);
}
/**
 * How far from the zone centre a health pack may be declared right now: a fixed
 * fraction of the arena, clamped, and never more than half the live zone.
 */
export function healthPackRadius(world) {
    const zone = world.zone;
    const liveRadius = zone.suppressed ? zone.startRadius : zone.radius;
    return Math.min(liveRadius * 0.5, clamp(world.map.startRadius * HEALTH_PACK_RADIUS_FRACTION, HEALTH_PACK_RADIUS_MIN, HEALTH_PACK_RADIUS_MAX));
}
/**
 * Each ready pickup goes to the NEAREST eligible body in reach. Ties go to the
 * first in `world.players` order, which is sorted by id — never to whoever's
 * packet arrived first.
 */
function collect(world, loot, events) {
    const reach = PICKUP_RADIUS + bodyRadius(world);
    const reach2 = reach * reach;
    const half = bodyHalfHeight(world);
    // Compacted in place: `kept` is where the next pickup that stays is written.
    const pickups = loot.pickups;
    let kept = 0;
    for (const pickup of pickups) {
        if (world.tick < pickup.readyTick) {
            pickups[kept++] = pickup;
            continue;
        }
        let taker = null;
        let best = Infinity;
        for (const p of world.players) {
            if (p.phase !== "alive" || p.health <= 0)
                continue;
            if (Math.abs(p.y - half - pickup.y) > PICKUP_VERTICAL_REACH)
                continue;
            const d2 = dist2XZ(p.x, p.z, pickup.x, pickup.z);
            if (d2 > reach2 || d2 >= best)
                continue;
            if (!wants(p, pickup.kind))
                continue;
            best = d2;
            taker = p;
        }
        if (taker === null) {
            pickups[kept++] = pickup;
            continue;
        }
        let taken = 1;
        if (pickup.kind === "health_pack") {
            taker.health = Math.min(maxHealth(taker), taker.health + HEALTH_PACK_HEAL);
        }
        else {
            // Take what fits under the cap; the rest of the stack stays on the floor
            // for somebody else.
            taken = Math.min(pickup.amount, COIN_CAP - taker.coins);
            taker.coins += taken;
            pickup.amount -= taken;
        }
        events.push({
            t: "pickup",
            tick: world.tick,
            playerId: taker.id,
            pickupId: pickup.id,
            kind: pickup.kind,
            amount: taken,
            x: pickup.x,
            y: pickup.y,
            z: pickup.z,
        });
        if (pickup.kind === "coin" && pickup.amount > 0)
            pickups[kept++] = pickup;
    }
    pickups.length = kept;
}
/** A full-health player walks over a health pack, and a capped one over a coin. */
function wants(p, kind) {
    // See `isGhost`: a pickup vanishing under nobody is a radar ping.
    if (isInvisible(p))
        return false;
    if (kind === "health_pack")
        return p.health < maxHealth(p);
    return p.coins < COIN_CAP;
}
/**
 * True for a living, invisible player.
 *
 * The loot block is PUBLIC — every viewer gets the same one — so anything an
 * invisible player does to it is a position leak the per-viewer snapshot
 * filter never sees: a pickup vanishing, a crate's health dropping, a decoy's
 * mirrored coin count ticking up. So while invisible you do not touch loot
 * at all. You cannot collect, your hits do not damage crates, and your rounds
 * pass straight through them.
 */
export function isGhost(world, id) {
    const p = findPlayer(world, id);
    return p !== null && p.phase === "alive" && isInvisible(p);
}
// ---------------------------------------------------------------------------
// Wire and audit
// ---------------------------------------------------------------------------
export function lootSnapshot(world) {
    const loot = world.loot;
    if (!loot)
        return undefined;
    return {
        crates: loot.crates.map((v) => ({ id: v.id, x: v.x, y: v.y, z: v.z, health: v.health })),
        pickups: loot.pickups.map((p) => ({ id: p.id, kind: p.kind, x: p.x, y: p.y, z: p.z, amount: p.amount })),
    };
}
/** Lines for `serializeWorld`. `num` is the world's exact-double formatter. */
export function serializeLoot(loot, num) {
    const out = [`loot=${loot.rngState},${loot.nextId},${loot.nextHealthPackTick}`];
    for (const v of loot.crates)
        out.push(`v=${v.id},${v.slot},${num(v.x)},${num(v.y)},${num(v.z)},${num(v.health)}`);
    for (const p of loot.pickups) {
        out.push(`pk=${p.id},${p.kind},${num(p.x)},${num(p.y)},${num(p.z)},${p.amount},${p.readyTick}`);
    }
    for (const r of loot.restocks)
        out.push(`rs=${r.slot},${r.tick}`);
    return out;
}
//# sourceMappingURL=loot.js.map